import React, { memo } from "react";
import classes from "./ButtonCard.scss";
// import { Announcements3D } from "@toddle-edu/ds-icons";
import { Badge, ToggleSwitch } from "@toddle-edu/ds-web";
import classNames from "classnames";

// Utils
import { onEnterOrSpaceKeyDown } from "Utils/AccessibilityHelpers";

const DEFAULT_LAYOUT_VARIANT = "default";

const ButtonCard = props => {
  const {
    count = 0,
    onClick,
    label,
    containerStyle,
    subLabel,
    icon,
    iconStyle,
    badgeText,
    rightIcon,
    isGlobalHeaderPresent = false,
    subLabelStyle = {},
    rightIconStyle = {},
    viewType = DEFAULT_LAYOUT_VARIANT, // default || list || card || toggle
    isSentralNotificationActive = false,
    isToggleChecked = false,
    ariaLabel,
    isKeyboardFocusable = true,
  } = props;

  const isDefaultView = viewType === "default";
  const isToggleView = viewType === "toggle";
  const isCardView = viewType === "card";
  const isListView = viewType === "list";

  // clicking toggle button triggers the click event twice because of the toggle
  // so we stop propagation on the toggle button click
  const handleToggleClick = e => {
    e.stopPropagation();
  };

  const containerClasses = classNames({
    [classes.container]: true,
    [classes.containerV2]: !isGlobalHeaderPresent,
    [classes.listViewContainer]: isListView,
    [classes.cardViewContainer]: isCardView,
    [classes.toggleViewContainer]: isToggleView,
  });

  const iconContainerClasses = classNames({
    [classes.iconContainer]: true,
    [classes.iconContainerV2]: !isGlobalHeaderPresent,
  });

  const leftContainerClasses = classNames(classes.leftContainer, {
    [classes.leftContainerListView]: isListView,
    [classes.leftContainerToggleView]: isToggleView,
    [classes.leftContainerCardView]: isCardView,
  });

  const rightContainerClasses = classNames({
    [classes.rightContainer]: true,
    [classes.rightContainerV2]: !isGlobalHeaderPresent,
    [classes.rightContainerCardView]: isCardView,
    [classes.rightContainerListOrToggleView]: isListView || isToggleView,
  });

  const rightContainerLabelContainerClasses = classNames({
    [classes.label]: true,
    [classes.labelV2]: isGlobalHeaderPresent,
    [classes.toggleDisabledLabel]: isToggleView && !isToggleChecked,
  });

  const rightContainerSubLabelContainerClasses = classNames({
    [classes.subLabel]: !isGlobalHeaderPresent,
    [classes.subLabelV2]: isGlobalHeaderPresent,
    "text-body-s": isGlobalHeaderPresent,
  });

  const badgeComponent = isGlobalHeaderPresent ? (
    <Badge
      type="numeric"
      value={count}
      variant="notifications"
      size="xxx-small"
      dsVersion="2.0"
    />
  ) : (
    <Badge type="numeric" value={count} variant="notifications" size="large" />
  );

  const sentralBadgeComponent = (
    <Badge
      type="dot"
      size="xx-small"
      dsVersion="2.0"
      color="critical"
      variant="active"
      shouldAnimateActiveDotBadge={true}
    />
  );

  return (
    <div
      data-test-id={props["data-test-id"]}
      className={containerClasses}
      onClick={onClick}
      style={containerStyle}
      role="button"
      tabIndex={isKeyboardFocusable ? 0 : -1}
      aria-label={ariaLabel}
      onKeyDown={onClick ? e => onEnterOrSpaceKeyDown(e, onClick) : undefined}
    >
      <div className={classes.rightIconContainer} style={rightIconStyle}>
        {isDefaultView && isSentralNotificationActive ? (
          <div className={classes.sentralBadgeWrapper}>
            {sentralBadgeComponent}
          </div>
        ) : null}

        {isDefaultView ? (
          <div className={classes.badgeWrapper}>{badgeComponent}</div>
        ) : null}

        {rightIcon && <div className={classes.rightIcon}>{rightIcon}</div>}
      </div>
      {icon && (
        <div className={leftContainerClasses}>
          <div className={iconContainerClasses} style={{ ...iconStyle }}>
            {icon}

            {isCardView ? (
              <div className={classes.cardViewBadgeContainer}>
                {badgeComponent}
                {isSentralNotificationActive ? sentralBadgeComponent : null}
              </div>
            ) : null}
          </div>
        </div>
      )}
      <div className={rightContainerClasses}>
        <div className={classes.labelContainer}>
          <div className={classes.labelBadgeContainer}>
            <div className={rightContainerLabelContainerClasses}>{label}</div>
            {badgeText && <div className={classes.badge}>{badgeText}</div>}
          </div>
          {subLabel && (
            <div
              className={rightContainerSubLabelContainerClasses}
              style={subLabelStyle}
            >
              {subLabel}
            </div>
          )}
        </div>

        {isListView ? (
          <>
            {badgeComponent}
            {isSentralNotificationActive ? sentralBadgeComponent : null}
          </>
        ) : null}

        {isToggleView ? (
          <ToggleSwitch
            dsVersion="2.0"
            size="medium"
            testId={props["data-test-id"]}
            onClick={handleToggleClick}
            checked={isToggleChecked}
          />
        ) : null}
      </div>
    </div>
  );
};

export default memo(ButtonCard);
